import React, { useState, useEffect } from "react";

// --- Context Imports ---
import { useBuildings } from "../../Contexts/BuildingContext";
import { useUnits } from "../../Contexts/UnitContext";
import { useTenantContext } from "../Tenants/TenantContext";

// --- Modal Imports ---
import FilterBuildingModal from "../Properties/FilterBuildingModal";
import UnitModal from "../Reports/components/UnitModal";
// import TenantModal from "../Reports/components-TenantModal";
import TenantModal from "../Reports/components/TenantModal";

// This component encapsulates the filter logic and UI
const Filters = ({
  onFilterChange,
  availableFilters = ["building", "unit", "tenant"],
  selectedFilters,
}) => {
  // --- Consume Contexts ---
  const { allBuildings, fetchAllBuildings, isLoadingBuildings } =
    useBuildings();
  const { allUnits, fetchAllUnits, isLoadingAllUnits } = useUnits();
  const { tenantData, fetchAllTenants, isLoadingTenants } = useTenantContext();

  // --- Local state for modal visibility ---
  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);

  // --- Fetch initial data ---
  useEffect(() => {
    if (!allBuildings.length && !isLoadingBuildings) fetchAllBuildings();
    if (!allUnits.length && !isLoadingAllUnits) fetchAllUnits();
    if (!tenantData.length && !isLoadingTenants) fetchAllTenants();
  }, []); // Runs once on mount

  // --- Handler functions to update parent state ---
  const handleBuildingFilter = (selectedBuildingIds) => {
    onFilterChange({ ...selectedFilters, buildingIds: selectedBuildingIds });
    setShowBuildingModal(false);
  };

  const handleUnitFilter = (selectedUnitIds) => {
    onFilterChange({ ...selectedFilters, unitIds: selectedUnitIds });
    setShowUnitModal(false);
  };

  const handleTenantFilter = (selectedTenantIds) => {
    onFilterChange({ ...selectedFilters, tenantIds: selectedTenantIds });
    setShowTenantModal(false);
  };

  // --- UPDATED: This function is now triggered by clicking the pill itself ---
  const handleClearSpecificFilter = (filterKey, idToRemove) => {
    onFilterChange({
      ...selectedFilters,
      [filterKey]: selectedFilters[filterKey].filter((id) => id !== idToRemove),
    });
  };

  // --- Centralized configuration for each filter type ---
  const filterConfig = {
    building: {
      label: "Buildings",
      isLoading: isLoadingBuildings,
      count: selectedFilters.buildingIds.length,
      onClick: () => setShowBuildingModal(true),
      colorClasses: "bg-blue-500 text-white border-blue-500",
      pillClasses: "bg-blue-100 text-blue-800",
      icon: "🏢",
    },
    unit: {
      label: "Units",
      isLoading: isLoadingAllUnits,
      count: selectedFilters.unitIds.length,
      onClick: () => setShowUnitModal(true),
      colorClasses: "bg-green-500 text-white border-green-500",
      pillClasses: "bg-green-100 text-green-800",
      icon: "🏠",
    },
    tenant: {
      label: "Tenants",
      isLoading: isLoadingTenants,
      count: selectedFilters.tenantIds.length,
      onClick: () => setShowTenantModal(true),
      colorClasses: "bg-purple-500 text-white border-purple-500",
      pillClasses: "bg-purple-100 text-purple-800",
      icon: "👤",
    },
  };

  return (
    <>
      {/* 1. Filter Buttons */}
      <div className="flex flex-wrap items-center gap-2">
        {availableFilters.map((filterKey) => {
          const config = filterConfig[filterKey];
          if (!config) return null;

          return (
            <button
              key={filterKey}
              className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                config.count > 0
                  ? config.colorClasses
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
              onClick={config.onClick}
              disabled={config.isLoading}
            >
              {config.label} {config.count > 0 && `(${config.count})`}
              {config.isLoading && "..."}
            </button>
          );
        })}
      </div>

      {/* 2. Active Filter Pills Display */}
      {(selectedFilters.buildingIds.length > 0 ||
        selectedFilters.unitIds.length > 0 ||
        selectedFilters.tenantIds.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {/* Building Pills */}
          {selectedFilters.buildingIds.map((id) => {
            const building = allBuildings.find((b) => b.entity_id === id);
            return building ? (
              <span
                key={id}
                title={`Click to remove: ${building.building_name}`}
                onClick={() => handleClearSpecificFilter("buildingIds", id)}
                className={`${filterConfig.building.pillClasses} inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer transition-colors hover:bg-red-200 hover:text-red-800`}
              >
                {filterConfig.building.icon} {building.building_name}
              </span>
            ) : null;
          })}

          {/* Unit Pills */}
          {selectedFilters.unitIds.map((id) => {
            const unit = allUnits.find((u) => u.entity_id === id);
            return unit ? (
              <span
                key={id}
                title={`Click to remove: ${unit.unit_name}`}
                onClick={() => handleClearSpecificFilter("unitIds", id)}
                className={`${filterConfig.unit.pillClasses} inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer transition-colors hover:bg-red-200 hover:text-red-800`}
              >
                {filterConfig.unit.icon} {unit.unit_name}
              </span>
            ) : null;
          })}

          {/* Tenant Pills */}
          {selectedFilters.tenantIds.map((id) => {
            const tenant = tenantData.find((t) => t.entity_id === id);
            return tenant ? (
              <span
                key={id}
                title={`Click to remove: ${tenant.first_name} ${tenant.last_name}`}
                onClick={() => handleClearSpecificFilter("tenantIds", id)}
                className={`${filterConfig.tenant.pillClasses} inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer transition-colors hover:bg-red-200 hover:text-red-800`}
              >
                {filterConfig.tenant.icon} {tenant.first_name}{" "}
                {tenant.last_name}
              </span>
            ) : null;
          })}
        </div>
      )}

      {/* 3. Modals */}
      <FilterBuildingModal
        show={showBuildingModal}
        handleClose={() => setShowBuildingModal(false)}
        buildings={allBuildings}
        selectedBuildingIds={selectedFilters.buildingIds}
        handleApply={handleBuildingFilter}
      />
      <UnitModal
        show={showUnitModal}
        handleClose={() => setShowUnitModal(false)}
        units={allUnits}
        selectedUnitIds={selectedFilters.unitIds}
        handleApply={handleUnitFilter}
      />
      <TenantModal
        show={showTenantModal}
        handleClose={() => setShowTenantModal(false)}
        tenantData={tenantData}
        selectedTenantIds={selectedFilters.tenantIds}
        handleApply={handleTenantFilter}
      />
    </>
  );
};

export default Filters;
